// filepath: apps/web/src/features/admin/effectsMetrics/MetricsTable.tsx
//
// FR-UI-ADM-9 AC-1 — per-channel metrics table with expand/collapse
// per-endpoint breakdown. Renders success rate %, retry count,
// dead-letter count, latency p50/p95/p99. Endpoint URLs are
// rendered as text nodes only (INV-9).

import React from "react";
import { useTranslation } from "react-i18next";
import type { ChannelMetrics, MetricsResponse } from "./types";

interface MetricsTableProps {
  metrics: MetricsResponse | undefined;
}

function successRate(ch: ChannelMetrics): number {
  const total = ch.success + ch.retry + ch.dead_letter;
  if (total === 0) return 0;
  return Math.round((ch.success / total) * 1000) / 10;
}

export function MetricsTable({
  metrics,
}: MetricsTableProps): React.ReactElement {
  const { t } = useTranslation();
  const channels = metrics?.global ?? {};

  return (
    <table
      className="effects-metrics-table"
      aria-label={t(
        "admin.effects.metrics.table_label",
        "Effect channel metrics",
      )}
    >
      <thead>
        <tr>
          <th scope="col">
            {t("admin.effects.metrics.column.channel_name", "Channel")}
          </th>
          <th scope="col">
            {t("admin.effects.metrics.column.delivery_count", "Deliveries")}
          </th>
          <th scope="col">
            {t("admin.effects.metrics.column.success_rate", "Success %")}
          </th>
          <th scope="col">
            {t("admin.effects.metrics.column.retry_count", "Retries")}
          </th>
          <th scope="col">
            {t(
              "admin.effects.metrics.column.dead_letter_count",
              "Dead-letters",
            )}
          </th>
          <th scope="col">
            {t("admin.effects.metrics.column.latency_p50", "p50 ms")}
          </th>
          <th scope="col">
            {t("admin.effects.metrics.column.latency_p95", "p95 ms")}
          </th>
          <th scope="col">
            {t("admin.effects.metrics.column.latency_p99", "p99 ms")}
          </th>
        </tr>
      </thead>
      <tbody>
        {(Object.keys(channels) as Array<keyof MetricsByTenant>).map((name) => {
          const ch = channels[name];
          if (!ch) return null;
          return (
            <tr key={String(name)}>
              <th scope="row">{String(name)}</th>
              <td>{ch.success + ch.retry + ch.dead_letter}</td>
              <td>{successRate(ch)}%</td>
              <td>{ch.retry}</td>
              <td>{ch.dead_letter}</td>
              <td>{ch.latency_p50_ms ?? "—"}</td>
              <td>{ch.latency_p95_ms ?? "—"}</td>
              <td>{ch.latency_p99_ms ?? "—"}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

type MetricsByTenant = NonNullable<MetricsResponse["global"]>;
